import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Download, Loader2, Printer, RotateCcw } from 'lucide-react';
import { api } from '@/lib/api';
import {
  imprimirVolumes,
  useImpressaoExpedicao,
  type EstadoImpressao,
  type Impressa,
} from '@/hooks/useImpressaoExpedicao';
import { cn } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { CabecalhoPagina, Cartao, CorpoCartao } from '@/componentes/ui/primitivos';
import type { VolumeDanfe } from '@/componentes/expedicao/DanfeSimplificada';

/**
 * Impressão de etiquetas de expedição NESTE computador — liga, desliga e
 * mostra o que está acontecendo.
 *
 * Quem imprime não é esta página: é o ProvedorImpressaoExpedicao, no
 * AppShell, que roda em qualquer tela do WMS. Aqui é a configuração (qual
 * crachá esta máquina imprime), o teste, a reimpressão e a fila parada.
 */

interface Pendente {
  registro: number;
  televenda: number;
  pedidaEm: string | null;
  motivo: 'outro_layout' | 'sem_dados';
  rtm: string | null;
}

const EXEMPLO: VolumeDanfe = {
  volume: '1 / 1',
  ean: '0001234567000000000000000012345600001',
  nfe: 'NFe: 123456',
  serie: 'Série: 1',
  movimento: new Date().toLocaleDateString('pt-BR'),
  chaveNfe: '31261013477477000619550220001234561000000000',
  nomeRem: 'MEGAMAMUTE COMERCIO ONLINE ELETRONICOS LTDA',
  enderecoRem: 'TESTE DE IMPRESSAO',
  cidadeRem: 'EXTREMA',
  estadoRem: 'UF.: MG',
  cepRem: 'CEP.: 00000-000',
  cnpjRem: '00.000.000/0000-00',
  ieRem: '0000000000000',
  total: null,
  nomeDes: 'NOME: TESTE DE IMPRESSAO',
  enderecoDes: 'END: CONFIRA SE A ETIQUETA SAIU INTEIRA E CENTRALIZADA',
  complementoDes: 'COMPL:',
  referencia: 'REF:',
  cidadeDes: 'CIDADE.: -',
  estadoDes: 'UF: --',
  bairroDes: 'BAIRRO: -',
  cepDes: 'CEP.: 00000-000',
  pedidoMarketplace: 'TESTE',
  transportadora: 'TESTE',
  televenda: 0,
};

const hora = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '—';

export function ImpressaoEtiquetas(): JSX.Element {
  const { cracha, crachaDaSessao, desligadoAqui, definirDesligadoAqui, estado, historico, assumir } =
    useImpressaoExpedicao();

  if (!crachaDaSessao) {
    return (
      <Moldura>
        <Cartao>
          <CorpoCartao className="text-[13px] text-textoFraco">
            Nenhum crachá em uso nesta sessão, então nenhuma etiqueta é impressa aqui. Informe o
            crachá na tela de Faturamento (ou vincule no cadastro do Procfit) e as etiquetas dele
            passam a sair sozinhas assim que o pedido é faturado.
          </CorpoCartao>
        </Cartao>
        <Instrucoes cracha={null} />
      </Moldura>
    );
  }

  if (desligadoAqui || !cracha) {
    return (
      <Moldura>
        <Cartao>
          <CorpoCartao className="flex flex-wrap items-center gap-3">
            <p className="min-w-0 flex-1 text-[13px] text-textoFraco">
              A impressão de etiquetas está <b className="text-texto">desligada neste computador</b>.
              As etiquetas do crachá {crachaDaSessao} ficam na fila e saem no computador onde ele
              estiver logado com a impressão ligada.
            </p>
            <Botao variante="primaria" onClick={() => definirDesligadoAqui(false)}>
              Ligar neste computador
            </Botao>
          </CorpoCartao>
        </Cartao>
      </Moldura>
    );
  }

  if (estado.tipo === 'desativada') {
    return (
      <Moldura>
        <Cartao>
          <CorpoCartao className="text-[13px] text-textoFraco">
            A impressão de etiquetas pelo WMS está <b className="text-texto">desligada pelo
            administrador</b>. As etiquetas continuam saindo pelo ERPM_ETIQUETAS.exe de cada
            checkout.
          </CorpoCartao>
        </Cartao>
      </Moldura>
    );
  }

  return (
    <Moldura>
      <Situacao
        cracha={cracha}
        estado={estado}
        aoDesligar={() => definirDesligadoAqui(true)}
        aoAssumir={assumir}
      />
      <Pendentes cracha={cracha} />
      <Historico historico={historico} />
      <Instrucoes cracha={cracha} />
    </Moldura>
  );
}

function Moldura({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="mx-auto max-w-[820px] space-y-4 p-4 sm:p-6">
      <CabecalhoPagina
        icone={<Printer className="h-5 w-5" />}
        sobrelinha="Este computador"
        titulo="Impressão de etiquetas"
        descricao="A etiqueta de expedição (DANFE simplificado) do seu crachá sai sozinha na Zebra assim que o pedido é faturado, com o WMS aberto em qualquer tela. Substitui o ERPM_ETIQUETAS.exe."
      />
      {children}
    </div>
  );
}

function Situacao({
  cracha,
  estado,
  aoDesligar,
  aoAssumir,
}: {
  cracha: string;
  estado: EstadoImpressao;
  aoDesligar: () => void;
  aoAssumir: () => void;
}): JSX.Element {
  const navegar = useNavigate();
  const erro = estado.tipo === 'erro' || estado.tipo === 'outro_computador';
  const [testando, definirTestando] = React.useState(false);

  return (
    <Cartao className={cn(erro && 'border-status-divergencia')}>
      <CorpoCartao className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={cn(
              'grid h-11 w-11 shrink-0 place-items-center rounded-xl',
              erro ? 'bg-status-divergenciaFundo text-status-divergencia' : 'bg-status-livreFundo text-status-livre',
            )}
          >
            {erro ? (
              <AlertTriangle className="h-6 w-6" />
            ) : estado.tipo === 'imprimindo' ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <CheckCircle2 className="h-6 w-6" />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-bold">
              {estado.tipo === 'erro'
                ? 'Parado'
                : estado.tipo === 'outro_computador'
                  ? 'Saindo em outro computador'
                : estado.tipo === 'imprimindo'
                  ? 'Imprimindo…'
                  : estado.tipo === 'outra_aba'
                    ? 'Ligado — outra aba do WMS neste computador está imprimindo'
                    : 'Ligado — aguardando etiquetas'}
            </p>
            <p className="text-[12.5px] text-textoFraco">
              Crachá <span className="tabular font-mono font-bold text-texto">{cracha}</span>
              {estado.tipo === 'ok'
                ? ` · conferido às ${estado.ultimaVolta.toLocaleTimeString('pt-BR')}`
                : ''}
            </p>
          </div>
          <Botao
            tamanho="pequeno"
            carregando={testando}
            onClick={() => {
              definirTestando(true);
              void imprimirVolumes([EXEMPLO], true).finally(() => definirTestando(false));
            }}
          >
            Imprimir teste
          </Botao>
          <Botao tamanho="pequeno" variante="fantasma" onClick={aoDesligar}>
            Desligar neste computador
          </Botao>
        </div>
        {estado.tipo === 'outro_computador' ? (
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[13px] font-semibold text-status-divergencia">
            {estado.mensagem} Este computador não imprime enquanto isso.
            <Botao tamanho="pequeno" variante="perigo" onClick={aoAssumir}>
              Imprimir aqui
            </Botao>
          </div>
        ) : null}
        {estado.tipo === 'erro' ? (
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[13px] font-semibold text-status-divergencia">
            {estado.mensagem}
            {estado.codigo === 'sessao' ? (
              <Botao
                tamanho="pequeno"
                variante="perigo"
                onClick={() => navegar('/entrar', { state: { de: '/impressao-etiquetas' } })}
              >
                Entrar de novo
              </Botao>
            ) : null}
          </div>
        ) : null}
      </CorpoCartao>
    </Cartao>
  );
}

const MOTIVOS: Record<Pendente['motivo'], string> = {
  sem_dados: 'Nota ainda sem dados para a etiqueta — sai assim que tiver',
  outro_layout: 'Outro layout — continua com o .exe',
};

function Pendentes({ cracha }: { cracha: string }): JSX.Element | null {
  const consulta = useQuery<{ impressora: string | null; pendentes: Pendente[] }>({
    queryKey: ['etiquetas-expedicao-situacao', cracha],
    queryFn: () => api.get('/api/etiquetas-expedicao/situacao', { cracha }),
    refetchInterval: 30_000,
  });

  const pendentes = consulta.data?.pendentes ?? [];
  if (pendentes.length === 0) return null;

  return (
    <Cartao>
      <CorpoCartao className="space-y-2">
        <h2 className="text-[14px] font-bold">
          Paradas neste crachá <span className="text-textoFraco">({pendentes.length})</span>
        </h2>
        <ul className="divide-y divide-borda text-[12.5px]">
          {pendentes.map((p) => (
            <li key={p.registro} className="flex items-center justify-between gap-2 py-1.5">
              <span className="tabular font-mono font-semibold">{p.televenda}</span>
              <span className="text-textoFraco">{MOTIVOS[p.motivo]}</span>
              <span className="tabular text-textoSuave">{hora(p.pedidaEm)}</span>
            </li>
          ))}
        </ul>
      </CorpoCartao>
    </Cartao>
  );
}

function Historico({ historico }: { historico: Impressa[] }): JSX.Element {
  return (
    <Cartao>
      <CorpoCartao className="space-y-2">
        <h2 className="text-[14px] font-bold">Impressas neste computador</h2>
        {historico.length === 0 ? (
          <p className="text-[12.5px] text-textoFraco">Nenhuma ainda. Fature um pedido e a etiqueta sai sozinha.</p>
        ) : (
          <ul className="divide-y divide-borda text-[12.5px]">
            {historico.map((e) => (
              <li key={`${e.registro}-${e.impressaEm}`} className="flex items-center gap-3 py-1.5">
                <span className="tabular w-[80px] font-mono font-semibold">{e.televenda}</span>
                <span className="flex-1 truncate text-textoFraco">
                  {e.volumes[0]?.nfe ?? ''} · {e.volumes.length} volume(s)
                  {e.manual ? ' · manual' : ''}
                </span>
                <span className="tabular text-textoSuave">{hora(e.impressaEm)}</span>
                <Botao tamanho="micro" variante="fantasma" onClick={() => void imprimirVolumes(e.volumes)}>
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reimprimir
                </Botao>
              </li>
            ))}
          </ul>
        )}
      </CorpoCartao>
    </Cartao>
  );
}

/**
 * Baixa um .cmd que o próprio operador abre: pede administrador (UAC), baixa o
 * preparar-checkout.ps1 e roda com -Escolher — a lista de impressoras do
 * Windows aparece na janela e ele escolhe pelo número. O navegador não enxerga
 * as impressoras nem roda script; por isso o arquivo.
 */
function baixarConfigurador(origem: string, impressora: string, cracha: string): void {
  // Dentro do .cmd: % vira %% (senão o cmd expande); ' dobra (string do PowerShell); " sai.
  const texto = (s: string): string => s.replace(/"/g, '').replace(/'/g, "''").replace(/%/g, '%%');
  const ps =
    '[Net.ServicePointManager]::SecurityProtocol = [Net.SecurityProtocolType]::Tls12; ' +
    "$f = Join-Path $env:TEMP 'preparar-checkout.ps1'; " +
    `iwr '${texto(origem)}/preparar-checkout.ps1' -OutFile $f -UseBasicParsing; ` +
    `& $f -Url '${texto(origem)}' -Impressora '${texto(impressora)}' -Escolher`;
  const linhas = [
    '@echo off',
    `rem Configura este checkout para a etiqueta de expedicao sair pelo WMS (cracha ${texto(cracha)}).`,
    'rem Gerado em Impressao de etiquetas do WMS.',
    // Sem bloco ( ): o Chrome renomeia para "configurar-checkout (1).cmd" e o ) quebraria o if.
    'net session >nul 2>&1',
    'if not errorlevel 1 goto configurar',
    `powershell -NoProfile -Command "Start-Process -FilePath '%~f0' -Verb RunAs"`,
    'exit /b',
    ':configurar',
    `powershell -NoProfile -ExecutionPolicy Bypass -Command "${ps}"`,
    'echo.',
    'pause',
  ];
  const url = URL.createObjectURL(new Blob([linhas.join('\r\n') + '\r\n'], { type: 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'configurar-checkout.cmd';
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function Instrucoes({ cracha }: { cracha: string | null }): JSX.Element {
  // Sem crachá a configuração do computador vale do mesmo jeito: a sugestão vira a padrão do Windows.
  const { data } = useQuery<{ impressora: string | null }>({
    queryKey: ['etiquetas-expedicao-situacao', cracha],
    queryFn: () => api.get('/api/etiquetas-expedicao/situacao', { cracha }),
    enabled: cracha !== null,
  });
  const [copiado, definirCopiado] = React.useState(false);
  const origem = window.location.origin;
  // A impressora do cadastro vai no comando; vazia, o script usa a padrão do Windows.
  const impressora = (data?.impressora ?? '').replace(/'/g, "''");
  const comando =
    `iwr ${origem}/preparar-checkout.ps1 -OutFile $env:TEMP\\preparar-checkout.ps1 -UseBasicParsing; ` +
    `powershell -ExecutionPolicy Bypass -File $env:TEMP\\preparar-checkout.ps1 -Url ${origem}` +
    // Argumento vazio some no PowerShell 5.1: sem impressora, o parâmetro não vai.
    (impressora ? ` -Impressora '${impressora}'` : '');

  return (
    <details open={cracha === null} className="cartao px-5 py-3 text-[12.5px] leading-relaxed">
      <summary className="cursor-pointer font-semibold">Como configurar este computador (uma vez)</summary>
      <p className="mt-2 text-textoFraco">
        Faz o mesmo que o ERPM_ETIQUETAS.exe: abre sozinho com o Windows e imprime sem pedir
        confirmação, na impressora escolhida.{' '}
        {cracha ? (
          <>
            Sugestão do cadastro do crachá {cracha}:{' '}
            <b className="text-texto">{data?.impressora || 'nenhuma cadastrada — usa a padrão do Windows'}</b>.
          </>
        ) : (
          'Sugestão: a impressora padrão do Windows.'
        )}{' '}
        Não instala programa nenhum.
      </p>
      <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-textoFraco">
        <li>
          <Botao tamanho="pequeno" variante="primaria" onClick={() => baixarConfigurador(origem, data?.impressora ?? '', cracha ?? 'nenhum')}>
            <Download className="h-3.5 w-3.5" />
            Baixar configurador
          </Botao>{' '}
          e abra o <b>configurar-checkout.cmd</b> baixado. Se o Chrome ou o Windows avisar, clique em{' '}
          <b>Manter</b> / <b>Executar</b> (na tela azul "O Windows protegeu o computador":{' '}
          <b>Mais informações</b> &gt; <b>Executar assim mesmo</b>); no pedido de administrador,
          clique em <b>Sim</b>.
        </li>
        <li>
          Na janela preta aparece a lista das impressoras do Windows: digite o número da Zebra e
          Enter (só Enter usa a sugerida). Depois ele pergunta se o computador é só do checkout:
          com <b>S</b>, o Chrome comum também imprime sem diálogo, não só o atalho WMS.
        </li>
        <li>
          Feche todas as janelas do Chrome e abra o WMS pelo atalho <b>WMS</b> que apareceu na área
          de trabalho. Entre com o login deste crachá.
        </li>
        <li>
          Venha nesta tela e clique em <b>Imprimir teste</b>: tem de sair na Zebra, sem diálogo.
          Se a etiqueta sair cortada, ajuste o papel do driver da Zebra para 100 × 150 mm.
        </li>
        <li>Feche o ERPM_ETIQUETAS.exe deste computador e tire ele da inicialização do Windows.</li>
      </ol>
      <p className="mt-3 text-textoFraco">
        Sem o configurador (TI): PowerShell como administrador e o comando abaixo.
      </p>
      <div className="mt-2 flex items-start gap-2">
        <pre className="min-w-0 flex-1 whitespace-pre-wrap break-all rounded-lg bg-superficie2 p-2 font-mono text-[11px]">
          {comando}
        </pre>
        <Botao
          tamanho="pequeno"
          onClick={() => {
            void navigator.clipboard?.writeText(comando).then(() => {
              definirCopiado(true);
              window.setTimeout(() => definirCopiado(false), 2000);
            });
          }}
        >
          {copiado ? 'Copiado' : 'Copiar'}
        </Botao>
      </div>
      <p className="mt-2 text-[11.5px] text-textoSuave">
        Trocou a impressora? Rode o configurador de novo. Para desfazer, rode o
        mesmo comando com <code>-Desfazer</code> no fim. Nesse Chrome toda impressão do WMS vai
        direto para a Zebra — A4 (romaneio, kardex) se imprime em outro computador.
      </p>
    </details>
  );
}
